import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LeafletMap from '../components/MapComponents/LeafletMap';
import {
  FaRoute as FaRouteIcon,
  FaClock,
  FaRoad,
  FaMoneyBillWave,
  FaGasPump,
  FaMap,
  FaMoneyBillAlt,
  FaArrowLeft,
  FaArrowRight,
  FaMapMarkerAlt
} from 'react-icons/fa';
import '../css/tripsummary.css';
import Header from '../components/Common/Header';
import Feedback from '../components/Common/Feedback';
import { routeService } from '../services/api';
import Seo from '../components/Common/Seo';

const isDevelopment = process.env.NODE_ENV !== 'production';
const devLog = (...args) => {
  if (isDevelopment) {
    console.log(...args);
  }
};
const devWarn = (...args) => {
  if (isDevelopment) {
    console.warn(...args);
  }
};
const devError = (...args) => {
  if (isDevelopment) {
    console.error(...args);
  }
};

// ========== DISTANCE CALCULATION UTILITY FUNCTIONS ==========
/**
 * Calculate distance between two coordinates using Haversine formula
 */
const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Find the closest point on the route to a tollgate and calculate distance along route
 */
const calculateDistanceAlongRoute = (routeGeometry, tollgateLat, tollgateLng) => {
  if (!routeGeometry || routeGeometry.length < 2) return 0;
  
  // Find the closest point on the route to the tollgate
  let closestIndex = 0;
  let minDistance = Infinity;
  
  for (let i = 0; i < routeGeometry.length; i++) {
    const [lat, lng] = routeGeometry[i];
    const distance = calculateHaversineDistance(lat, lng, tollgateLat, tollgateLng);
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
    }
  }
  
  // Calculate distance from start to the closest point
  let accumulatedDistance = 0;
  for (let i = 1; i <= closestIndex; i++) {
    const [lat1, lng1] = routeGeometry[i - 1];
    const [lat2, lng2] = routeGeometry[i];
    accumulatedDistance += calculateHaversineDistance(lat1, lng1, lat2, lng2);
  }
  
  return accumulatedDistance;
};

/**
 * Calculate total route distance
 */
const calculateTotalRouteDistance = (routeGeometry) => {
  if (!routeGeometry || routeGeometry.length < 2) return 0;
  
  let totalDistance = 0;
  for (let i = 1; i < routeGeometry.length; i++) {
    const [lat1, lng1] = routeGeometry[i - 1];
    const [lat2, lng2] = routeGeometry[i];
    totalDistance += calculateHaversineDistance(lat1, lng1, lat2, lng2);
  }
  
  return totalDistance;
};

// ========== MAIN COMPONENT ==========
const TripSummaryPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const shareId = searchParams.get('share');

  const [activeTollgate, setActiveTollgate] = useState(null);
  const [activeFuelStation, setActiveFuelStation] = useState(null);
  const [fuelBrandFilter, setFuelBrandFilter] = useState('All');
  const [recommendedOnly, setRecommendedOnly] = useState(false);
  const [fuelStations, setFuelStations] = useState([]);
  const [fuelStationsMeta, setFuelStationsMeta] = useState(null);
  const [fuelStationsLoading, setFuelStationsLoading] = useState(false);
  const [fuelStationsError, setFuelStationsError] = useState('');
  const [showRouteOptionsModal, setShowRouteOptionsModal] = useState(false);
  const [sharedRouteData, setSharedRouteData] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');

  const routeData = location.state?.routeData;
  const seoTitle = shareId ? 'Shared Route Summary | Whe2' : 'Trip Summary | Whe2';
  const seoDescription = shareId
    ? 'View a shared Whe2 route summary with trip cost, toll and fuel breakdown.'
    : 'Review your selected route, toll breakdown and estimated trip cost.';

  useEffect(() => {
    let cancelled = false;

    const loadSharedRoute = async () => {
      if (!shareId) {
        setSharedRouteData(null);
        setShareError('');
        return;
      }

      setShareLoading(true);
      setShareError('');
      try {
        const response = await routeService.getSharedRoute(shareId);
        if (cancelled) return;
        setSharedRouteData(response?.data?.route || null);
      } catch (error) {
        if (cancelled) return;
        setSharedRouteData(null);
        setShareError(error?.message || 'Could not load this shared route.');
      } finally {
        if (!cancelled) setShareLoading(false);
      }
    };

    loadSharedRoute();

    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const linkedRoute = useMemo(() => {
    if (sharedRouteData) {
      return {
        route_name: sharedRouteData.route_name || 'Shared route',
        duration: sharedRouteData.duration || 'N/A',
        total_distance_km: sharedRouteData.total_distance_km || 'N/A',
        total_trip_cost: sharedRouteData.total_trip_cost != null ? `R${Number(sharedRouteData.total_trip_cost).toFixed(2)}` : 'R0.00',
        estimated_fuel_cost: sharedRouteData.estimated_fuel_cost != null ? `R${Number(sharedRouteData.estimated_fuel_cost).toFixed(2)}` : 'R0.00',
        total_toll_price_cost: sharedRouteData.total_toll_price_cost != null ? `R${Number(sharedRouteData.total_toll_price_cost).toFixed(2)}` : 'R0.00',
        tollgates: [],
        route_geometry: []
      };
    }

    const route = searchParams.get('route');
    const duration = searchParams.get('duration');
    const distance = searchParams.get('distance');
    const total = searchParams.get('total');
    const fuel = searchParams.get('fuel');
    const toll = searchParams.get('toll');

    if (!route && !duration && !distance && !total) return null;

    return {
      route_name: route || 'Shared route',
      duration: duration || 'N/A',
      total_distance_km: distance || 'N/A',
      total_trip_cost: total ? `R${Number(total).toFixed(2)}` : 'R0.00',
      estimated_fuel_cost: fuel ? `R${Number(fuel).toFixed(2)}` : 'R0.00',
      total_toll_price_cost: toll ? `R${Number(toll).toFixed(2)}` : 'R0.00',
      tollgates: [],
      route_geometry: []
    };
  }, [searchParams, sharedRouteData]);
  const destination = sharedRouteData?.end || location.state?.destination || searchParams.get('end') || 'Destination';
  const startLocation = sharedRouteData?.start || location.state?.startLocation || searchParams.get('start') || 'Start';
  const isExampleData = Boolean(location.state?.isExampleData);
  const apiConnected = Boolean(location.state?.apiConnected);
  const selectedFromOptions = Boolean(location.state?.selectedFromOptions);
  const selectedRouteIndex = Number(location.state?.selectedRouteIndex) || 0;
  const allRoutesData = location.state?.allRoutesData;

  const actualRoutes = useMemo(() => {
    return Array.isArray(allRoutesData?.routes)
      ? allRoutesData.routes
      : Array.isArray(routeData?.routes)
        ? routeData.routes
        : linkedRoute
          ? [linkedRoute]
        : [];
  }, [allRoutesData, linkedRoute, routeData]);

  const selectedRoute = useMemo(() => {
    return actualRoutes[selectedRouteIndex] ||
      actualRoutes[0] ||
      routeData?.routes?.[0] ||
      {};
  }, [actualRoutes, selectedRouteIndex, routeData]);

  const memoizedRouteGeometry = useMemo(() => {
    return selectedRoute?.route_geometry || [];
  }, [selectedRoute?.route_geometry]);

  const rawTollgates = useMemo(() => {
    return Array.isArray(selectedRoute?.tollgates) ? selectedRoute.tollgates : [];
  }, [selectedRoute?.tollgates]);

  // Order tollgates using backend normalized_position (preferred) or distance_along_route
  const orderedTollgates = useMemo(() => {
    const copy = rawTollgates.slice();

    const normalized = copy.map((t, i) => ({
      ...t,
      normalized_position: (typeof t.normalized_position === 'number') ? t.normalized_position : (t.distance_along_route != null ? t.distance_along_route : null),
      distance_along_route: (typeof t.distance_along_route === 'number') ? t.distance_along_route : (t.normalized_position != null ? t.normalized_position : null),
      sequence: t.sequence ?? t.seq ?? (i + 1),
    }));

    normalized.sort((a, b) => {
      const aKey = (typeof a.normalized_position === 'number') ? a.normalized_position : (typeof a.distance_along_route === 'number' ? a.distance_along_route : Number.POSITIVE_INFINITY);
      const bKey = (typeof b.normalized_position === 'number') ? b.normalized_position : (typeof b.distance_along_route === 'number' ? b.distance_along_route : Number.POSITIVE_INFINITY);
      return aKey - bKey;
    });

    return normalized.map((t, idx) => ({ ...t, __displayIndex: idx }));
  }, [rawTollgates]);

  // ========== CENTRALIZED DISTANCE CALCULATION ==========
  // Calculate distances for all tollgates ONCE in the parent
  const tollgatesWithDistances = useMemo(() => {
    if (!memoizedRouteGeometry.length || !orderedTollgates.length) return orderedTollgates;

    // Calculate total route distance once
    const totalRouteDistance = calculateTotalRouteDistance(memoizedRouteGeometry);
    
    return orderedTollgates.map(tollgate => {
      const tollgateLat = tollgate.latitude || tollgate.lat;
      const tollgateLng = tollgate.longitude || tollgate.lon;
      
      let distanceFromStart = null;
      let distanceToDestination = null;
      let progressPercentage = null;
      
      if (typeof tollgateLat === 'number' && typeof tollgateLng === 'number') {
        // Calculate distance from start ALONG THE ROUTE (not straight-line)
        distanceFromStart = calculateDistanceAlongRoute(
          memoizedRouteGeometry,
          tollgateLat,
          tollgateLng
        );
        
        // Calculate remaining distance
        if (totalRouteDistance > 0) {
          distanceToDestination = totalRouteDistance - distanceFromStart;
          progressPercentage = Math.round((distanceFromStart / totalRouteDistance) * 100);
        }
      }
      
      return {
        ...tollgate,
        // Round to 1 decimal place for display
        distanceFromStart: distanceFromStart !== null ? Math.round(distanceFromStart * 10) / 10 : null,
        distanceToDestination: distanceToDestination !== null ? Math.round(distanceToDestination * 10) / 10 : null,
        progressPercentage,
        // Include total route distance for cost calculations in child
        totalRouteDistance: Math.round(totalRouteDistance * 10) / 10,
      };
    });
  }, [orderedTollgates, memoizedRouteGeometry]);

  const hasValidRouteGeometry = useMemo(() => {
    return Array.isArray(memoizedRouteGeometry) && memoizedRouteGeometry.length >= 2;
  }, [memoizedRouteGeometry]);

  const hasTollgates = tollgatesWithDistances.length > 0;
  const availableFuelBrands = useMemo(() => {
    const brands = Array.from(
      new Set(
        fuelStations
          .map((station) => station.brand)
          .filter(Boolean)
      )
    );
    return brands.sort((a, b) => a.localeCompare(b));
  }, [fuelStations]);

  const filteredFuelStations = useMemo(() => {
    return fuelStations.filter((station) => {
      if (fuelBrandFilter !== 'All' && station.brand !== fuelBrandFilter) return false;
      if (recommendedOnly && !station.recommended) return false;
      return true;
    });
  }, [fuelBrandFilter, fuelStations, recommendedOnly]);
  const hasFuelStations = filteredFuelStations.length > 0;
  const averageFuelSpacing = useMemo(() => {
    if (filteredFuelStations.length < 2) return null;
    const gaps = [];
    for (let i = 1; i < filteredFuelStations.length; i += 1) {
      gaps.push(filteredFuelStations[i].distanceFromStart - filteredFuelStations[i - 1].distanceFromStart);
    }
    return Math.round(gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length);
  }, [filteredFuelStations]);

  useEffect(() => {
    let cancelled = false;

    const loadFuelStations = async () => {
      if (!hasValidRouteGeometry) {
        setFuelStations([]);
        setFuelStationsMeta(null);
        setFuelStationsError('');
        setFuelStationsLoading(false);
        return;
      }

      setFuelStationsLoading(true);
      setFuelStationsError('');

      try {
        const response = await routeService.getFuelStationsAlongRoute({
          route_geometry: memoizedRouteGeometry,
          max_detour_km: 5,
          max_stations: 24,
        });

        if (cancelled) return;

        setFuelStations(Array.isArray(response?.stations) ? response.stations : []);
        setFuelStationsMeta(response || null);
        setActiveFuelStation(null);
      } catch (error) {
        if (cancelled) return;
        setFuelStations([]);
        setFuelStationsMeta(null);
        setFuelStationsError(error?.message || 'Could not load filling stations for this route.');
      } finally {
        if (!cancelled) {
          setFuelStationsLoading(false);
        }
      }
    };

    loadFuelStations();

    return () => {
      cancelled = true;
    };
  }, [hasValidRouteGeometry, memoizedRouteGeometry]);

  useEffect(() => {
    if (activeFuelStation === null) return;
    if (activeFuelStation >= filteredFuelStations.length) {
      setActiveFuelStation(null);
    }
  }, [activeFuelStation, filteredFuelStations.length]);

  useEffect(() => {
    if (fuelBrandFilter !== 'All' && !availableFuelBrands.includes(fuelBrandFilter)) {
      setFuelBrandFilter('All');
    }
  }, [availableFuelBrands, fuelBrandFilter]);

  const calculatePerKmCost = useCallback(() => {
    try {
      const distanceStr = selectedRoute?.total_distance_km || '0';
      const costStr = selectedRoute?.total_trip_cost || '0';
      const distance = parseFloat(String(distanceStr).replace(/[^\d.]/g, ''));
      const cost = parseFloat(String(costStr).replace(/[^\d.]/g, ''));
      if (!isNaN(distance) && !isNaN(cost) && distance > 0) {
        return `R${(cost / distance).toFixed(2)}/km`;
      }
    } catch (e) {
      devWarn('Error calculating per km cost:', e);
    }
    return 'N/A';
  }, [selectedRoute]);

  const perKmCost = calculatePerKmCost();

  const handleSelectRouteFromModal = useCallback((index) => {
    if (index < 0 || index >= actualRoutes.length) {
      devError('Invalid route index:', index);
      return;
    }
    const newRoute = actualRoutes[index];
    const updatedRouteData = {
      trip_name: allRoutesData?.trip_name || `From ${startLocation} to ${destination}`,
      routes: [newRoute]
    };
    navigate('/trip-summary', {
      state: {
        routeData: updatedRouteData,
        destination,
        startLocation,
        isExampleData,
        apiConnected,
        selectedFromOptions: true,
        totalRoutes: actualRoutes.length,
        selectedRouteIndex: index,
        allRoutesData
      },
      replace: true
    });
    setShowRouteOptionsModal(false);
    setActiveTollgate(null);
  }, [actualRoutes, allRoutesData, startLocation, destination, isExampleData, apiConnected, navigate]);

  // Handle map marker clicks
  const handleTollgateMapClick = useCallback((tollgate, maybeSeqOrIndex) => {
    let resolvedIndex = -1;

    if (typeof maybeSeqOrIndex === 'number' && maybeSeqOrIndex >= 0 && maybeSeqOrIndex < tollgatesWithDistances.length) {
      resolvedIndex = maybeSeqOrIndex;
    } else if (tollgate && (tollgate.sequence || tollgate.sequence === 0)) {
      resolvedIndex = tollgatesWithDistances.findIndex(t => Number(t.sequence) === Number(tollgate.sequence));
    }

    if (resolvedIndex === -1 && tollgate) {
      resolvedIndex = tollgatesWithDistances.findIndex(t => {
        const sameName = (t.name || '').trim().toLowerCase() === (tollgate.name || '').trim().toLowerCase();
        const latA = Number(t.latitude ?? t.lat ?? t.latitude);
        const lonA = Number(t.longitude ?? t.lon ?? t.longitude);
        const latB = Number(tollgate.latitude ?? tollgate.lat ?? tollgate.latitude);
        const lonB = Number(tollgate.longitude ?? tollgate.lon ?? tollgate.longitude);
        const close = Math.abs(latA - latB) < 0.0005 && Math.abs(lonA - lonB) < 0.0005;
        return sameName || close;
      });
    }

    if (resolvedIndex === -1) return;

    devLog(`📌 Map marker clicked -> sidebar index ${resolvedIndex}`);

    setActiveTollgate(resolvedIndex);

    const tollgateElement = document.getElementById(`tollgate-${resolvedIndex}`);
    if (tollgateElement) {
      tollgateElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      tollgateElement.classList.add('highlighted');
      setTimeout(() => tollgateElement.classList.remove('highlighted'), 1500);
    }
  }, [tollgatesWithDistances]);

  const handleTollgateSidebarClick = useCallback((tollgate, index) => {
    devLog(`Sidebar clicked: ${tollgate.name} (index: ${index})`);
    if (activeTollgate === index) {
      setActiveTollgate(null);
      const el = document.getElementById(`tollgate-${index}`);
      if (el) el.classList.remove('active');
    } else {
      setActiveTollgate(index);
      const prev = document.getElementById(`tollgate-${activeTollgate}`);
      if (prev) prev.classList.remove('active');
      const el = document.getElementById(`tollgate-${index}`);
      if (el) {
        el.classList.add('active');
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [activeTollgate]);

  const handleFuelStationMapClick = useCallback((station, index) => {
    setActiveFuelStation(index);
    const stationElement = document.getElementById(`fuel-station-${index}`);
    if (stationElement) {
      stationElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      stationElement.classList.add('highlighted');
      setTimeout(() => stationElement.classList.remove('highlighted'), 1500);
    }
  }, []);

  const handleFuelStationSidebarClick = useCallback((station, index) => {
    setActiveFuelStation((prev) => prev === index ? null : index);
  }, []);

  const handlePreviousRoute = useCallback(() => {
    if (selectedRouteIndex > 0) handleSelectRouteFromModal(selectedRouteIndex - 1);
  }, [selectedRouteIndex, handleSelectRouteFromModal]);

  const handleNextRoute = useCallback(() => {
    if (selectedRouteIndex < actualRoutes.length - 1) handleSelectRouteFromModal(selectedRouteIndex + 1);
  }, [selectedRouteIndex, actualRoutes.length, handleSelectRouteFromModal]);

  useEffect(() => {
    devLog('Selected route:', selectedRoute?.route_name || selectedRoute);
    devLog('Tollgates with calculated distances:', tollgatesWithDistances.map(t => ({
      name: t.name,
      distanceFromStart: t.distanceFromStart,
      distanceToDestination: t.distanceToDestination,
      progressPercentage: t.progressPercentage
    })));
  }, [selectedRoute, tollgatesWithDistances]);

  useEffect(() => {
    const handlePopState = () => { if (!location.state) navigate('/'); };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, location.state]);

  // Modal omitted for brevity; keep your current implementation if needed
  const renderRouteOptionsModal = () => {
    if (!showRouteOptionsModal || actualRoutes.length <= 1) return null;
    return (
      <div className="route-options-modal-overlay" onClick={() => setShowRouteOptionsModal(false)}>
        <div className="route-options-modal" onClick={(e) => e.stopPropagation()}>
          {/* ...modal content (same as before) */}
        </div>
      </div>
    );
  };

  if (shareId && shareLoading && !sharedRouteData) {
    return (
      <div className="app-container">
        <Header />
        <Feedback />
        <main className="content trip-summary-content">
          <div className="card main-trip-card">
            <div className="trip-header-section">
              <div className="trip-title-badge"><FaRouteIcon size={14} /><span>Trip Summary</span></div>
              <h2 className="trip-title">Loading shared route...</h2>
              <div className="trip-route-subtitle">Please wait while Whe2 loads this route.</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (shareId && shareError && !sharedRouteData) {
    return (
      <div className="app-container">
        <Header />
        <Feedback />
        <main className="content trip-summary-content">
          <div className="card main-trip-card">
            <div className="trip-header-section">
              <div className="trip-title-badge"><FaRouteIcon size={14} /><span>Trip Summary</span></div>
              <h2 className="trip-title">Shared route unavailable</h2>
              <div className="trip-route-subtitle">{shareError}</div>
            </div>
            <div className="trip-actions">
              <button className="btn-primary trip-main-action" onClick={() => navigate('/', { state: { scrollTarget: 'download' } })} aria-label="Download Whe2">Download Whe2</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={shareId ? `/trip-summary?share=${encodeURIComponent(shareId)}` : '/trip-summary'}
        robots="noindex,follow"
      />
      <Header />
      <Feedback />
      <main className="content trip-summary-content">
        {/* main trip card */}
        <div className="card main-trip-card">
          <div className="trip-header-section">
            <div className="trip-title-badge"><FaRouteIcon size={14} /><span>Trip Summary</span></div>
            <h2 className="trip-title">{selectedRoute.route_name || 'Unnamed Route'}</h2>
            <div className="trip-route-subtitle">{startLocation.split(',')[0] || 'Start'} → {destination.split(',')[0] || 'End'}</div>
          </div>

          <div className="trip-key-stats">
            <div className="key-stat"><div className="key-stat-icon"><FaClock size={14} /></div><div className="key-stat-content"><div className="key-stat-value">{selectedRoute.duration || 'N/A'}</div><div className="key-stat-label">Duration</div></div></div>
            <div className="key-stat"><div className="key-stat-icon"><FaRoad size={14} /></div><div className="key-stat-content"><div className="key-stat-value">{selectedRoute.total_distance_km || 'N/A'}</div><div className="key-stat-label">Distance</div></div></div>
            <div className="key-stat"><div className="key-stat-icon"><FaMoneyBillWave size={14} /></div><div className="key-stat-content"><div className="key-stat-value">{selectedRoute.total_trip_cost || 'N/A'}</div><div className="key-stat-label">Total Cost</div></div></div>
          </div>

          {perKmCost !== 'N/A' && (
            <div className="cost-efficiency-badge"><FaMoneyBillAlt size={12} /><span className="efficiency-label">Cost efficiency:</span><span className="efficiency-value">{perKmCost}</span></div>
          )}
        </div>

        <div className="card map-card-with-sidebar">
          <div className="card-header">
            <h3 className="section-title"><FaMap className="section-icon" />Route Map {hasTollgates && `• ${tollgatesWithDistances.length} Tollgates`}</h3>

            {selectedFromOptions && actualRoutes.length > 1 && (
              <div className="map-action-buttons">
                <div className="route-navigation-controls">
                  <button className={`map-action-btn ${selectedRouteIndex === 0 ? 'disabled' : ''}`} onClick={handlePreviousRoute} disabled={selectedRouteIndex === 0}><FaArrowLeft size={12} /></button>
                  <div className="route-position-indicator">Route {selectedRouteIndex + 1} of {actualRoutes.length}</div>
                  <button className={`map-action-btn ${selectedRouteIndex === actualRoutes.length - 1 ? 'disabled' : ''}`} onClick={handleNextRoute} disabled={selectedRouteIndex === actualRoutes.length - 1}><FaArrowRight size={12} /></button>
                </div>
              </div>
            )}
          </div>

          <div className="map-sidebar-container">
            <div className="map-sidebar-left">
              {hasValidRouteGeometry ? (
                <div className="map-container-full">
                  {/* PASS THE PRE-CALCULATED DISTANCES TO LEAFETMAP */}
                  <LeafletMap
                    routeGeometry={memoizedRouteGeometry}
                    tollgates={tollgatesWithDistances} // Pass the enriched tollgates
                    fuelStations={filteredFuelStations}
                    tripName={selectedRoute.route_name}
                    height="450px"
                    onTollgateClick={handleTollgateMapClick}
                    onFuelStationClick={handleFuelStationMapClick}
                    activeTollgate={activeTollgate}
                    activeFuelStation={activeFuelStation}
                    startAddress={startLocation}
                    endAddress={destination}
                  />
                </div>
              ) : (
                <div className="map-placeholder">
                  <FaMap size={50} className="placeholder-icon" />
                  <div className="placeholder-text">Route Map Unavailable</div>
                  <div className="placeholder-subtext">{tollgatesWithDistances.length || 0} tollgates on this route</div>
                </div>
              )}
            </div>

            <div className="tollgates-sidebar">
              <div className="tollgates-sidebar-header">
                <h4 className="sidebar-title"><FaRoad className="sidebar-icon" />Tollgate List</h4>
                {hasTollgates && <div className="sidebar-count">{tollgatesWithDistances.length} total</div>}
              </div>

              <div className="tollgates-list-container">
                {hasTollgates ? (
                  <div className="tollgates-list">
                    {tollgatesWithDistances.map((tollgate, index) => (
                      <div
                        key={tollgate.sequence ?? index}
                        id={`tollgate-${index}`}
                        className={`tollgate-sidebar-item ${activeTollgate === index ? 'active' : ''}`}
                        onClick={() => handleTollgateSidebarClick(tollgate, index)}
                      >
                        <div className="sidebar-item-header">
                          <div className="sidebar-item-sequence"><div className="sequence-circle">{index + 1}</div></div>
                          <div className="sidebar-item-content">
                            <div className="sidebar-tollgate-name">{tollgate.name || `Tollgate ${index + 1}`}</div>
                            <div className="sidebar-tollgate-details">
                              {/* Show distance from start (pre-calculated in parent) */}
                              {tollgate.distanceFromStart !== null && (
                                <div className="distance-from-start">
                                  <FaMapMarkerAlt className="distance-icon" size={10} />
                                  <span className="distance-label">
                                    ~{tollgate.distanceFromStart} km from start
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="sidebar-item-price">
                            <div className="price-amount">R{typeof tollgate.price === 'number' ? tollgate.price.toFixed(2) : (tollgate.price || '0')}</div>
                            <div className="price-label">Cost</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-tollgates-sidebar">
                    <FaRoad className="no-tollgates-icon" size={32} />
                    <h4>No Tollgates</h4>
                    <p>This route has no toll fees.</p>
                  </div>
                )}
              </div>

              {hasTollgates && (
                <div className="tollgates-summary">
                  <div className="summary-row"><span className="summary-label">Total Tollgates:</span><span className="summary-value">{tollgatesWithDistances.length}</span></div>
                  <div className="summary-row"><span className="summary-label">Total Cost:</span><span className="summary-value">{selectedRoute.total_toll_price_cost || 'R0.00'}</span></div>
                  <div className="summary-row"><span className="summary-label">Average Cost:</span><span className="summary-value">R{(tollgatesWithDistances.reduce((sum, toll) => { const price = typeof toll.price === 'number' ? toll.price : Number(toll.price || 0); return sum + price; }, 0) / tollgatesWithDistances.length).toFixed(2)}</span></div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card fuel-stations-card">
          <div className="card-header fuel-stations-header">
            <h3 className="section-title"><FaGasPump className="section-icon" />Filling Stations Along Route</h3>
            {hasFuelStations && (
              <div className="fuel-count-badge">
                {filteredFuelStations.length} stations
                {averageFuelSpacing ? <span className="avg-distance">avg. {averageFuelSpacing} km apart</span> : null}
              </div>
            )}
          </div>

          <div className="fuel-stations-controls">
            <div className="brand-filter">
              <span>Brand</span>
              <select
                className="brand-select"
                value={fuelBrandFilter}
                onChange={(e) => {
                  setFuelBrandFilter(e.target.value);
                  setActiveFuelStation(null);
                }}
              >
                <option value="All">All brands</option>
                {availableFuelBrands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>

            <label className="toggle-switch">
              <span className="toggle-label">Recommended only</span>
              <span className="switch">
                <input
                  type="checkbox"
                  checked={recommendedOnly}
                  onChange={(e) => {
                    setRecommendedOnly(e.target.checked);
                    setActiveFuelStation(null);
                  }}
                />
                <span className="slider" />
              </span>
            </label>
          </div>

          {fuelStationsLoading ? (
            <div className="no-fuel-stations">
              <FaGasPump size={32} />
              <h4>Loading filling stations</h4>
              <p>Searching for real stations within 5 km of the selected route.</p>
            </div>
          ) : fuelStationsError ? (
            <div className="no-fuel-stations">
              <FaGasPump size={32} />
              <h4>Fuel station search unavailable</h4>
              <p>{fuelStationsError}</p>
            </div>
          ) : hasFuelStations ? (
            <>
              <div className="fuel-stations-container">
                {filteredFuelStations.map((station, index) => (
                  <div
                    key={station.id}
                    id={`fuel-station-${index}`}
                    className={`fuel-station-item ${activeFuelStation === index ? 'active' : ''}`}
                    onClick={() => handleFuelStationSidebarClick(station, index)}
                  >
                    <div className="station-item-header">
                      <div className="station-brand-circle" style={{ background: station.recommended ? 'linear-gradient(135deg, #0f766e, #14b8a6)' : 'linear-gradient(135deg, #1d4ed8, #60a5fa)' }}>
                        <FaGasPump size={11} color="#fff" />
                      </div>
                      <div className="station-item-content">
                        <div className="station-name">
                          {station.name}
                          <span className="station-brand-tag">{station.brand}</span>
                          {station.recommended ? <span className="station-brand-tag">Recommended stop</span> : null}
                        </div>
                        <div className="station-details">
                          <span className="station-distance">{station.distanceFromStart} km from start</span>
                          <span>{station.distanceToDestination} km to go</span>
                          <span>Detour {station.detourKm} km</span>
                        </div>
                        <div className="station-address">{station.address}</div>
                        <div className="station-services-full">
                          {station.services.map((service) => <span key={`${station.id}-${service}`}>{service}</span>)}
                        </div>
                      </div>
                      <div className="station-action">
                        <button className="view-station-btn" type="button">View on map</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="fuel-stations-summary">
                <div className="summary-row"><span className="summary-label">Source:</span><span className="summary-value">{fuelStationsMeta?.source === 'google_places' ? 'Google Places corridor search' : 'Live route search'}</span></div>
                <div className="summary-row"><span className="summary-label">Route corridor:</span><span className="summary-value">Within 5 km of the route</span></div>
                <div className="summary-row"><span className="summary-label">Active filter:</span><span className="summary-value">{fuelBrandFilter === 'All' ? 'All brands' : fuelBrandFilter}{recommendedOnly ? ' • Recommended only' : ''}</span></div>
                <div className="summary-row"><span className="summary-label">Search anchors:</span><span className="summary-value">{fuelStationsMeta?.search_anchor_count || 0} route checks</span></div>
                <div className="summary-row"><span className="summary-label">Best planning use:</span><span className="summary-value">Compare real refuel options before departure</span></div>
              </div>
            </>
          ) : (
            <div className="no-fuel-stations">
              <FaGasPump size={32} />
              <h4>No stations match this filter</h4>
              <p>{fuelStations.length > 0 ? 'Try a different brand or turn off the recommended-only filter.' : 'No stations were found within 5 km of this route.'}</p>
            </div>
          )}
        </div>

        <div className="card cost-breakdown">
          <h3 className="section-title"><FaMoneyBillWave className="section-icon" />Cost Breakdown</h3>
          <div className="cost-breakdown-grid">
            <div className="breakdown-item fuel-breakdown">
              <div className="breakdown-header"><FaGasPump className="breakdown-icon" size={16} />
                <div className="breakdown-info"><div className="breakdown-title">Fuel Cost</div><div className="breakdown-subtitle">{selectedRoute.fuel_type || 'Petrol'} • R{selectedRoute.fuel_price_used?.toFixed(2) || 'N/A'}/L</div></div>
              </div>
              <div className="breakdown-amount">{selectedRoute.estimated_fuel_cost || 'N/A'}</div>
            </div>

            <div className="breakdown-item toll-breakdown">
              <div className="breakdown-header"><FaRoad className="breakdown-icon" size={16} />
                <div className="breakdown-info"><div className="breakdown-title">Toll Cost</div><div className="breakdown-subtitle">{tollgatesWithDistances.length || 0} tollgate{tollgatesWithDistances.length !== 1 ? 's' : ''}</div></div>
              </div>
              <div className="breakdown-amount">{selectedRoute.total_toll_price_cost || 'R0.00'}</div>
            </div>
          </div>

          <div className="total-cost-summary"><div className="total-cost-label">Total Trip Cost</div><div className="total-cost-value">{selectedRoute.total_trip_cost || 'N/A'}</div></div>
        </div>

        <div className="trip-actions">
          <button className="btn-primary trip-main-action" onClick={() => navigate('/', { state: { scrollTarget: 'download' } })} aria-label="Download Whe2">Download Whe2</button>
        </div>

        {renderRouteOptionsModal()}
      </main>
    </div>
  );
};

export default TripSummaryPage;
